import React, { useEffect, useState } from "react";
import {
  Activity,
  BarChart3,
  Calendar,
  ChevronRight,
  Search,
  RefreshCcw,
  Users,
  Star,
} from "lucide-react";
import { format } from "date-fns";
import { useApi } from "../../hooks/useApi";
import { StatCard } from './admin-dashboard/StatCard';
import { SessionTrendsChart } from './admin-dashboard/charts/SessionTrendsChart';
import { CounselorPerformanceChart } from './admin-dashboard/charts/CounselorPerformanceChart';
import { AttendanceDistributionChart } from './admin-dashboard/charts/AttendanceDistributionChart';
import { DashboardFilters } from './admin-dashboard/filters/DashboardFilters';
import ReportsSection from "./ReportsSection";  // Import ReportsSection

const COLORS = {
  primary: "#003087",
  secondary: "#FFD100",
  accent: "#DC3545",
  success: "#28A745",
  warning: "#FFC107",
  info: "#17A2B8",
};

export default function AdminDashboard() {
  const { get, post } = useApi();
  const [searchQuery, setSearchQuery] = useState('');
  const [state, setState] = useState({
    stats: null,
    trends: [],
    counselors: [],
    clients: [],
    attendance: [],
    loading: true,
    error: null,
    lastUpdated: null,
  });
  const [filters, setFilters] = useState(null);

  const fetchData = async (filterParams = {}) => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      const [stats, trends, counselors, clients, attendance] = await Promise.all([
        get("/dashboard/stats/admin"),
        get("/dashboard/sessions/trends", { params: filterParams }),
        get("/dashboard/counselors/performance", { params: filterParams }),
        get("/dashboard/clients/progress", { params: filterParams }),
        get("/dashboard/attendance/rates", { params: filterParams }),
      ]);

      setState({
        stats: stats.data.data,
        trends: trends.data.data,
        counselors: counselors.data.data,
        clients: clients.data.data,
        attendance: attendance.data.data,
        loading: false,
        error: null,
        lastUpdated: new Date(),
      });
    } catch (error) {
      console.error("Dashboard Data Fetch Error:", error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message,
      }));
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(filters), 300000); // 5-minute auto-refresh
    return () => clearInterval(interval);
  }, [filters]);

  // Error State
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <Activity className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-medium mb-2">Error Loading Dashboard</h2>
            <p className="text-gray-600 mb-4">{state.error}</p>
            <button
              onClick={() => fetchData(filters)}
              className="bg-uzima-maroon-500 text-white px-4 py-2 rounded-lg hover:bg-uzima-maroon-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welcome back</h1>
            <p className="text-gray-500">Counseling System Overview</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-uzima-maroon-400 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <span className="text-sm text-gray-500">
              Last updated: {state.lastUpdated ? format(state.lastUpdated, "MMM d, h:mm a") : "Never"}
            </span>
            <button
              onClick={() => fetchData(filters)}
              disabled={state.loading}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              <RefreshCcw className={`h-5 w-5 ${state.loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <DashboardFilters
          onFilter={setFilters}
          onExport={(format) => {/* Implement export handling */}}
          isLoading={state.loading}
        />

        {/* Main Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column - Stats and Charts */}
          <div className="col-span-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              <StatCard
                title="Total Users"
                value={state.stats?.totalUsers}
                icon={Users}
                isLoading={state.loading}
                trend={5}
                color={COLORS.primary}
              />
              <StatCard
                title="Active Counselors"
                value={state.stats?.activeCounselors}
                icon={Calendar}
                isLoading={state.loading}
                color={COLORS.secondary}
              />
            </div>

            {/* Session Trends */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-uzima-maroon-500" />
                  <h2 className="font-semibold text-gray-900">Session Trends</h2>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Details</span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div className="p-6">
                <SessionTrendsChart data={state.trends} isLoading={state.loading} />
              </div>
            </div>

            {/* Counselor Performance */}
            <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
              <CounselorPerformanceChart data={state.counselors} isLoading={state.loading} />
            </div>
          </div>

          {/* Right Column - Status, Attendance, Reports */}
          <div className="col-span-4 space-y-6">
            {/* System Performance */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium mb-6 flex items-center">
                <Activity className="h-5 w-5 mr-2" style={{ color: COLORS.primary }} />
                System Performance
              </h2>
              <div className="space-y-6">
                {/* Session Completion Rate */}
                {/* <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Session Completion
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {state.stats?.systemMetrics?.sessionCompletion}%
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-uzima-maroon-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${state.stats?.systemMetrics?.sessionCompletion || 0}%`,
                      }}
                    />
                  </div>
                </div> */}

                {/* Average Rating */}
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">
                      Average Rating
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {state.stats?.systemMetrics?.averageRating}/5
                    </span>
                  </div>
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= state.stats?.systemMetrics?.averageRating
                            ? "text-yellow-400 fill-current"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Attendance Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <AttendanceDistributionChart data={state.attendance} isLoading={state.loading} />
            </div>

            {/* Active Counselors */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-medium mb-6">Active Counselors</h2>
              <div className="space-y-4">
                {state.counselors.slice(0, 3).map((counselor, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium">
                        {counselor.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{counselor.name}</p>
                        {/* <p className="text-sm text-gray-500">
                          {counselor.completionRate}% completion rate
                        </p> */}
                      </div>
                    </div>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        counselor.completionRate >= 80 ? "bg-green-500" : "bg-yellow-500"
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Reports Section (New Addition) */}
            <ReportsSection />
          </div>
        </div>
      </div>
    </div>
  );
}
